import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listReturns, reviewReturn } from '@/api/returns';
import type { ReturnsQuery } from '@/config/env';
import { toast } from '@/store/useToastStore';
import { PageHeader } from '@/components/ui/PageHeader';
import { ExportControls } from '@/components/ui/ExportControls';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { SelectField } from '@/components/ui/SelectField';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Pagination } from '@/components/ui/Pagination';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { ErrorState, LoadingState } from '@/components/feedback/StateViews';
import { useAsyncData } from '@/hooks/useAsyncData';
import { REFUND_MODE_OPTIONS, RETURN_STATUS_TABS, reasonLabel, refundModeLabel } from '@/constants/defaults';
import { lastNDaysRange } from '@/lib/dateRange';
import type { DateRange } from '@/lib/dateRange';
import { formatCurrency, formatDate, formatNumber } from '@/lib/format';
import { returnAmount } from '@/lib/returnRow';
import { cn } from '@/lib/cn';
import type { Page, ReturnRecord } from '@/types';
import { paths } from '@/router/paths';

const PAGE_SIZE = 20;

/** Millisecond timestamp from an ISO string or a Java `[y, m, d, …]` array. */
function timeOf(value: string | number[] | undefined): number {
  if (Array.isArray(value)) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    return new Date(year, (month ?? 1) - 1, day ?? 1, hour, minute, second).getTime();
  }
  return value ? Date.parse(value) : 0;
}

export function ReturnsListScreen() {
  const navigate = useNavigate();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [refundMode, setRefundMode] = useState('');
  const [exchangeFilter, setExchangeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [sortNewest, setSortNewest] = useState(true);
  const [page, setPage] = useState(0);
  const [range, setRange] = useState<DateRange>(() => lastNDaysRange(30));
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const activeTab = RETURN_STATUS_TABS[activeTabIndex];
  /** Bulk approve/reject is only meaningful for requests still awaiting a decision. */
  const isBulkTab = activeTab.status === 'PENDING_REVIEW';
  const gridCols = isBulkTab
    ? 'grid-cols-[32px_1.2fr_0.9fr_1fr_1.8fr_0.8fr_0.9fr_0.8fr_1fr]'
    : 'grid-cols-[1.2fr_0.9fr_1fr_1.8fr_0.8fr_0.9fr_0.8fr_1fr]';

  const query = useMemo<ReturnsQuery>(
    () => ({
      status: activeTab.status,
      statuses: activeTab.statuses,
      refundMode: refundMode || undefined,
      isExchange: exchangeFilter === '' ? undefined : exchangeFilter === 'true',
      page,
      size: PAGE_SIZE,
      from: range.from,
      to: range.to,
    }),
    [activeTab, refundMode, exchangeFilter, page, range],
  );

  const { data, errorMessage, isLoading, reload } = useAsyncData<Page<ReturnRecord> | null>(
    (signal) => listReturns(query, signal),
    [query],
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = [...(data?.content ?? [])];
    if (term) {
      list = list.filter((row) =>
        [row.returnId, row.shopifyOrderNumber, row.customerName, row.customerEmail]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(term)),
      );
    }
    list.sort((first, second) =>
      sortNewest
        ? timeOf(second.createdAt) - timeOf(first.createdAt)
        : timeOf(first.createdAt) - timeOf(second.createdAt),
    );
    return list;
  }, [data, search, sortNewest]);

  // Clear any selection when the underlying result set changes (tab / page / filters).
  useEffect(() => {
    setSelectedIds(new Set());
  }, [query]);

  const selectableIds = useMemo(
    () => (isBulkTab ? rows.map((row) => row.returnId) : []),
    [isBulkTab, rows],
  );
  const selectedCount = selectedIds.size;
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const toggleOne = (returnId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(returnId)) {
        next.delete(returnId);
      } else {
        next.add(returnId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((current) => (current.size === selectableIds.length ? new Set() : new Set(selectableIds)));
  };

  const runBulkReview = async (action: 'APPROVED' | 'REJECTED') => {
    const ids = selectableIds.filter((id) => selectedIds.has(id));
    if (ids.length === 0) {
      return;
    }
    
    setBulkBusy(true);
    const results = await Promise.allSettled(ids.map((id) => reviewReturn(id, action)));
    const succeeded = results.filter((result) => result.status === 'fulfilled').length;
    const failed = ids.length - succeeded;
    setBulkBusy(false);
    setSelectedIds(new Set());
    if (succeeded > 0) {
      toast.success(`${succeeded} return${succeeded > 1 ? 's' : ''} ${action === 'APPROVED' ? 'approved' : 'rejected'}.`);
    }
    if (failed > 0) {
      toast.error(`${failed} return${failed > 1 ? 's' : ''} could not be updated.`);
    }
    reload();
  };

  return (
    <div className="animate-fade-in pb-10">
      <PageHeader
        title="Returns"
        description="Review and action customer return requests."
        actions={
          <>
            <DateRangePicker
              value={range}
              onChange={(nextRange) => {
                setRange(nextRange);
                setPage(0);
              }}
            />
            <ExportControls
              type="return"
              filenamePrefix="returns"
              range={range}
              status={activeTab.status}
              statuses={activeTab.statuses}
            />
          </>
        }
      />

      {/* Status tabs */}
      <div className="mb-4 overflow-x-auto border-b border-line">
        <div className="flex min-w-max gap-1">
          {RETURN_STATUS_TABS.map((tab, index) => {
            const isActive = index === activeTabIndex;
            return (
              <button
                key={tab.label}
                type="button"
                onClick={() => {
                  setActiveTabIndex(index);
                  setPage(0);
                }}
                className={cn(
                  'flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-ink text-ink'
                    : 'border-transparent text-ink-muted hover:text-ink',
                )}
              >
                {tab.label}
                {isActive && data ? (
                  <span
                    className={cn(
                      'rounded-full px-1.5 py-0.5 text-xs font-semibold',
                      tab.label === 'QC failed' ? 'bg-danger-soft text-danger' : 'bg-surface-sunken text-ink-muted',
                    )}
                  >
                    {formatNumber(data.totalElements)}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* Toolbar */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
              <circle cx="7" cy="7" r="5" />
              <path d="m11 11 3 3" strokeLinecap="round" />
            </svg>
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by request ID, order, customer…"
            className="h-10 w-full rounded-lg border border-line bg-surface pl-9 pr-3 text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand"
          />
        </div>
        <SelectField
          aria-label="Refund mode filter"
          value={refundMode}
          onChange={(event) => {
            setRefundMode(event.target.value);
            setPage(0);
          }}
          options={[
            { value: '', label: 'Refund mode' },
            ...REFUND_MODE_OPTIONS.map((mode) => ({ value: mode, label: refundModeLabel(mode) })),
          ]}
        />
        <SelectField
          aria-label="Type filter"
          value={exchangeFilter}
          onChange={(event) => {
            setExchangeFilter(event.target.value);
            setPage(0);
          }}
          options={[
            { value: '', label: 'Returns & exchanges' },
            { value: 'false', label: 'Returns only' },
            { value: 'true', label: 'Exchanges only' },
          ]}
        />
        <button
          type="button"
          onClick={() => setSortNewest((current) => !current)}
          className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-line bg-surface px-3 text-sm font-medium text-ink transition-colors hover:bg-surface-sunken"
        >
          <span className="text-ink-subtle">{sortNewest ? '↓' : '↑'}</span>
          Sort: {sortNewest ? 'Newest' : 'Oldest'}
        </button>
      </div>

      {/* Bulk action bar */}
      {isBulkTab && selectedCount > 0 ? (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-brand/30 bg-brand/5 px-4 py-2.5">
          <span className="text-sm font-medium text-ink">
            {selectedCount} selected
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-sm font-medium text-ink-muted hover:text-ink"
            >
              Clear
            </button>
            <Button
              variant="danger"
              onClick={() => runBulkReview('REJECTED')}
              disabled={bulkBusy}
            >
              Reject
            </Button>
            <Button
              variant="success"
              onClick={() => runBulkReview('APPROVED')}
              disabled={bulkBusy}
            >
              Approve
            </Button>
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <LoadingState message="Loading returns…" />
      ) : errorMessage ? (
        <ErrorState message={errorMessage} onRetry={reload} />
      ) : (
        <div className="flex flex-col gap-4">
          <Card padded={false} className="overflow-hidden">
            <div className="overflow-x-auto">
              <div className="min-w-[960px]">
                {/* Header row */}
                <div
                  className={cn(
                    'grid items-center gap-3 border-b border-line bg-surface-sunken/60 px-4 py-3',
                    gridCols,
                  )}
                >
                  {isBulkTab ? (
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="h-4 w-4 cursor-pointer rounded border-line accent-brand"
                    />
                  ) : null}
                  <HeaderCell>Request</HeaderCell>
                <HeaderCell>Date</HeaderCell>
                  <HeaderCell>Customer</HeaderCell>
                  <HeaderCell>Items</HeaderCell>
                  <HeaderCell>Reason</HeaderCell>
                  <HeaderCell>Refund mode</HeaderCell>
                  <HeaderCell>Amount</HeaderCell>
                  <HeaderCell>Status</HeaderCell>
                </div>

                {rows.length === 0 ? (
                  <p className="px-4 py-12 text-center text-sm text-ink-muted">No returns match these filters.</p>
                ) : (
                  <ul className="divide-y divide-line">
                    {rows.map((row) => {
                      const firstItem = row.items?.[0];
                      const itemCount = row.items?.length ?? 0;
                      const amount = returnAmount(row);
                      return (
                        <li
                          key={row.returnId}
                          onClick={() => navigate(paths.returnDetail(row.returnId))}
                          className={cn(
                            'grid cursor-pointer items-center gap-3 px-4 py-3.5 transition-colors hover:bg-surface-sunken/40',
                            gridCols,
                            selectedIds.has(row.returnId) ? 'bg-brand/5' : '',
                          )}
                        >
                          {isBulkTab ? (
                            <input
                              type="checkbox"
                              aria-label={`Select ${row.returnId}`}
                              checked={selectedIds.has(row.returnId)}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => toggleOne(row.returnId)}
                              className="h-4 w-4 cursor-pointer rounded border-line accent-brand"
                            />
                          ) : null}
                          <div className="min-w-0">
                            <div className="font-mono text-sm font-semibold text-ink">{row.returnId}</div>
                            {row.shopifyOrderNumber ? (
                              <div className="truncate font-mono text-xs text-ink-subtle">
                                {row.shopifyOrderNumber}
                              </div>
                            ) : null}
                            {row.isMerchantManual ? <Badge tone="progress">Manual Return</Badge> : null}
                          </div>

                          <div className="truncate text-sm text-ink-muted">{formatDate(row.createdAt) || '—'}</div>

                          <div className="flex min-w-0 items-center gap-3">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-semibold text-ink">
                                {row.customerName || '—'}
                              </div>
                              <div className="truncate text-xs text-ink-muted">
                                {row.customerEmail ?? '—'}
                              </div>
                              <div className="truncate text-xs text-ink-muted">
                                {row.customerPhone ?? '—'}
                              </div>
                            </div>
                          </div>

                          <div className="flex min-w-0 items-center gap-3">
                            {firstItem?.imageUrl ? (
                              <img
                                src={firstItem.imageUrl}
                                alt=""
                                className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
                              />
                            ) : (
                              <span className="h-10 w-10 flex-shrink-0 rounded-lg bg-surface-sunken" />
                            )}
                            <div className="min-w-0">
                              <div className="truncate text-sm text-ink">
                                {firstItem?.productTitle ?? '—'}
                              </div>
                              {itemCount > 1 ? (
                                <div className="truncate text-xs text-ink-subtle">+{itemCount - 1} more</div>
                              ) : null}
                            </div>
                          </div>

                          <div className="truncate text-sm text-ink-muted">{reasonLabel(row.reasonSlug)}</div>

                          <div className="truncate text-sm text-ink-muted">
                            {row.isExchange ? 'Exchange' : refundModeLabel(row.refundMode)}
                          </div>

                          <div className="text-sm font-semibold text-ink">
                            {amount != null ? formatCurrency(amount) : '—'}
                          </div>

                          <div>
                            <StatusBadge status={row.status} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </Card>

          {data && data.totalPages > 0 ? (
            <Pagination
              page={data.page ?? page}
              totalPages={data.totalPages}
              totalElements={data.totalElements}
              onPageChange={setPage}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function HeaderCell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={cn('text-xs font-semibold uppercase tracking-wide text-ink-subtle', className)}>
      {children}
    </span>
  );
}
